// src/types/inventory.ts

export enum UnitType {
  KG = 'kg',
  G = 'g',
  LITER = 'liter',
  ML = 'ml',
  PIECE = 'piece',
}

export enum MaterialCategory {
  VEGETABLES = 'vegetables',
  FRUITS = 'fruits',
  MEATS = 'meats',
  POULTRY = 'poultry',
  DAIRY = 'dairy',
  GRAINS = 'grains',
  LEGUMES = 'legumes',
  SPICES = 'spices',
  OILS_FATS = 'oils_fats',
  BEVERAGES = 'beverages',
  OTHER = 'other',
}

export enum StockMovementType {
  ORDER_DEDUCTION = 'order_deduction',
  ORDER_RESTORE = 'order_restore',
  MANUAL_RESTOCK = 'manual_restock',
  MANUAL_ADJUSTMENT = 'manual_adjustment',
  UNRECONCILED_DEFICIT = 'unreconciled_deficit',
}

export interface Material {
  id: string;
  restaurantId: string;
  name: string;
  category?: MaterialCategory | null;
  quantity: number;
  price: number;
  previousRestockCost?: number | null;
  lastRestockCost?: number | null;
  unitType: UnitType;
  lowStockThreshold?: number | null;
  isActive: boolean;
  createdAt?: string;
  updatedAt?: string;
}

export interface RecipeTemplate {
  id: string;
  restaurantId: string;
  name: string;
  description?: string;
  items?: RecipeTemplateItem[];
  createdAt?: string;
  updatedAt?: string;
}

export interface RecipeTemplateItem {
  id: string;
  templateId: string;
  materialId: string;
  quantity: number;
  unitType: UnitType;
  material?: Material;
  createdAt?: string;
  updatedAt?: string;
}

export interface StockMovement {
  id: string;
  materialId: string;
  orderId?: string | null;
  movementType: StockMovementType;
  quantity: number;
  previousStock: number;
  newStock: number;
  unitCost?: number | null;
  effectiveDate?: string | null;
  notes?: string;
  createdAt?: string;
  updatedAt?: string;
}

export interface InventorySummary {
  total: number;
  inStock: number;
  lowStock: number;
  outOfStock: number;
  needsReconciliation: number;
}

// Anomaly detection
export interface MaterialAnomaly {
  id: string;
  name: string;
  quantity: number;
  unitType: UnitType;
  category?: string | null;
  lastDeductionAt: string | null;
  suggestedRestockQuantity: number | null;
  estimatedDeficit: number;
}

// Batch restock
export interface BatchRestockItem {
  materialId: string;
  quantity: number;
  price?: number;
  effectiveDate?: string;
}

export interface BatchRestockResult {
  results: { materialId: string; success: true; name: string; newQuantity: number }[];
  errors: { materialId: string; success: false; error: string }[];
  totalSuccess: number;
  totalErrors: number;
}

// DTOs
export interface CreateMaterialDto {
  name: string;
  quantity?: number;
  price?: number;
  unitType: UnitType;
  category?: MaterialCategory | null;
  lowStockThreshold?: number | null;
}

export interface UpdateMaterialDto {
  name?: string;
  price?: number;
  unitType?: UnitType;
  category?: MaterialCategory | null;
  lowStockThreshold?: number | null;
  isActive?: boolean;
}

export interface CreateRecipeTemplateDto {
  name: string;
  description?: string;
  items: {
    materialId: string;
    quantity: number;
    unitType: UnitType;
  }[];
}

export interface UpdateRecipeTemplateDto {
  name?: string;
  description?: string | null;
  items?: {
    id?: string;
    materialId: string;
    quantity: number;
    unitType: UnitType;
  }[];
}

// Unit labels
export const UNIT_LABELS: Record<UnitType, { ar: string; en: string }> = {
  [UnitType.KG]: { ar: 'كيلوغرام', en: 'Kilogram' },
  [UnitType.G]: { ar: 'غرام', en: 'Gram' },
  [UnitType.LITER]: { ar: 'لتر', en: 'Liter' },
  [UnitType.ML]: { ar: 'مليلتر', en: 'Milliliter' },
  [UnitType.PIECE]: { ar: 'قطعة', en: 'Piece' },
};

export const UNIT_SHORT_LABELS: Record<UnitType, { ar: string; en: string }> = {
  [UnitType.KG]: { ar: 'كغم', en: 'kg' },
  [UnitType.G]: { ar: 'غم', en: 'g' },
  [UnitType.LITER]: { ar: 'لتر', en: 'L' },
  [UnitType.ML]: { ar: 'مل', en: 'ml' },
  [UnitType.PIECE]: { ar: 'قطعة', en: 'pc' },
};

export const MATERIAL_CATEGORY_LABELS: Record<MaterialCategory, { ar: string; en: string }> = {
  [MaterialCategory.VEGETABLES]: { ar: 'خضروات', en: 'Vegetables' },
  [MaterialCategory.FRUITS]: { ar: 'فواكه', en: 'Fruits' },
  [MaterialCategory.MEATS]: { ar: 'لحوم', en: 'Meats' },
  [MaterialCategory.POULTRY]: { ar: 'دواجن', en: 'Poultry' },
  [MaterialCategory.DAIRY]: { ar: 'ألبان وبيض', en: 'Dairy & Eggs' },
  [MaterialCategory.GRAINS]: { ar: 'حبوب ونشويات', en: 'Grains & Starches' },
  [MaterialCategory.LEGUMES]: { ar: 'بقوليات', en: 'Legumes' },
  [MaterialCategory.SPICES]: { ar: 'بهارات وتوابل', en: 'Spices & Seasonings' },
  [MaterialCategory.OILS_FATS]: { ar: 'زيوت ودهون', en: 'Oils & Fats' },
  [MaterialCategory.BEVERAGES]: { ar: 'مشروبات', en: 'Beverages' },
  [MaterialCategory.OTHER]: { ar: 'أخرى', en: 'Other' },
};

export const MOVEMENT_TYPE_LABELS: Record<StockMovementType, { ar: string; en: string }> = {
  [StockMovementType.ORDER_DEDUCTION]: { ar: 'خصم طلب', en: 'Order Deduction' },
  [StockMovementType.ORDER_RESTORE]: { ar: 'استعادة طلب', en: 'Order Restore' },
  [StockMovementType.MANUAL_RESTOCK]: { ar: 'تعبئة مخزون', en: 'Manual Restock' },
  [StockMovementType.MANUAL_ADJUSTMENT]: { ar: 'تعديل يدوي', en: 'Manual Adjustment' },
  [StockMovementType.UNRECONCILED_DEFICIT]: { ar: 'عجز غير مسوّى', en: 'Unreconciled Deficit' },
};

